import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import SEOMeta from './SEOMeta';

const EFFECTIVE_DATE = 'April 1, 2026';
const COMPANY_NAME = 'NivasaPro Inc.';
const CONTACT_EMAIL = 'legal@nivasapro.ai';
const SITE_URL = 'https://www.nivasapro.ai';

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
      {title}
    </h2>
    <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
      {children}
    </div>
  </section>
);

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEOMeta
        title="Terms and Conditions"
        description="NivasaPro Terms and Conditions. Read before creating your account."
        canonical="/terms"
        noIndex
      />

      {/* Nav */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <RouterLink to="/" className="flex items-center space-x-2">
            <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-7 w-7" />
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              NivasaPro.ai
            </span>
          </RouterLink>
          <RouterLink
            to="/register"
            className="text-sm text-cyan-600 hover:underline font-medium"
          >
            ← Back to Registration
          </RouterLink>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Terms and Conditions</h1>
          <p className="text-sm text-gray-500">
            Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last updated: {EFFECTIVE_DATE}
          </p>
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            Please read these Terms and Conditions carefully before creating an account or using the
            NivasaPro platform. By registering you agree to be bound by these terms.
          </div>
        </div>

        {/* Table of Contents */}
        <div className="mb-10 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
          <h2 className="text-base font-semibold text-gray-800 mb-3">Contents</h2>
          <ol className="space-y-1 text-sm text-cyan-700 list-decimal list-inside">
            {[
              ['acceptance', 'Acceptance of Terms'],
              ['description', 'Description of Service'],
              ['ai-services', 'Agentic AI Services'],
              ['accounts', 'User Accounts and Registration'],
              ['acceptable-use', 'Acceptable Use'],
              ['data-privacy', 'Data, Privacy and Security'],
              ['ip', 'Intellectual Property'],
              ['payment', 'Subscription and Payment'],
              ['disclaimers', 'Disclaimers and Warranties'],
              ['liability', 'Limitation of Liability'],
              ['indemnification', 'Indemnification'],
              ['termination', 'Termination'],
              ['governing-law', 'Governing Law'],
              ['changes', 'Changes to These Terms'],
              ['contact', 'Contact Information'],
            ].map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="hover:underline">
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </div>

        {/* Sections */}
        <Section id="acceptance" title="1. Acceptance of Terms">
          <p>
            These Terms and Conditions ("Terms") constitute a legally binding agreement between you
            ("User", "you", or "your") and {COMPANY_NAME} ("NivasaPro", "we", "us", or "our")
            governing your access to and use of the NivasaPro platform, website, mobile
            applications, APIs, and related services (collectively, the "Service") accessible at{' '}
            {SITE_URL}.
          </p>
          <p>
            By creating an account, clicking "I agree", or otherwise accessing or using the Service,
            you acknowledge that you have read, understood, and agree to be bound by these Terms and
            our Privacy Policy. If you do not agree, you must not access or use the Service.
          </p>
          <p>
            If you are using the Service on behalf of a company or other legal entity, you represent
            that you have authority to bind that entity to these Terms.
          </p>
        </Section>

        <Section id="description" title="2. Description of Service">
          <p>
            NivasaPro is an AI-powered property management platform designed to help landlords,
            property managers, and tenants manage residential and commercial properties. The Service
            includes, but is not limited to:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Property and tenant management tools</li>
            <li>AI-assisted maintenance request intake, triage, and dispatch ("Niva")</li>
            <li>Lease and payment tracking</li>
            <li>Service provider coordination</li>
            <li>Communication tools including SMS, WhatsApp, and in-app messaging</li>
            <li>Analytics and reporting dashboards</li>
          </ul>
          <p>
            The Service is provided on a software-as-a-service (SaaS) basis. We reserve the right
            to modify, suspend, or discontinue any aspect of the Service at any time with reasonable
            notice.
          </p>
        </Section>

        <Section id="ai-services" title="3. Agentic AI Services">
          <p>
            NivasaPro employs agentic artificial intelligence systems, including the "Niva" AI
            assistant, that can autonomously perform tasks on your behalf such as triaging
            maintenance requests, matching service providers, drafting communications, and initiating
            workflows. By using the Service you acknowledge and agree to the following:
          </p>
          <p className="font-medium text-gray-800">3.1 Nature of AI Outputs</p>
          <p>
            AI-generated content, decisions, summaries, and recommendations are produced by
            large language models and automated reasoning systems. These outputs may contain errors,
            omissions, or inaccuracies. You are responsible for reviewing all AI-generated content
            before acting on it. NivasaPro does not guarantee that AI outputs are accurate,
            complete, or suitable for any particular purpose.
          </p>
          <p className="font-medium text-gray-800">3.2 Autonomous Actions</p>
          <p>
            The AI agents within the Service may take autonomous actions including, but not limited
            to, creating maintenance records, sending emails and SMS messages, and notifying third
            parties. By enabling these features you expressly authorise NivasaPro to perform these
            actions on your behalf. You remain solely responsible for reviewing and approving
            consequential actions (such as vendor dispatch) where approval workflows are provided.
          </p>
          <p className="font-medium text-gray-800">3.3 Human Oversight</p>
          <p>
            You are responsible for maintaining appropriate human oversight of all agentic AI
            functions. NivasaPro provides approval workflows and audit logs for this purpose. You
            must not use the Service in situations where AI errors could result in personal injury,
            property damage, or other serious harm without implementing adequate human review
            processes.
          </p>
          <p className="font-medium text-gray-800">3.4 Third-Party AI Providers</p>
          <p>
            AI capabilities within the Service are powered in part by third-party providers
            including Google (Gemini) and Anthropic (Claude). Your use of AI features is also
            subject to the applicable terms and policies of those providers. We do not share your
            personally identifiable information with AI providers beyond what is necessary to
            deliver the Service.
          </p>
          <p className="font-medium text-gray-800">3.5 No Professional Advice</p>
          <p>
            AI-generated content does not constitute legal, financial, medical, building, safety, or
            any other professional advice. You should seek qualified professional advice for all
            decisions of consequence.
          </p>
        </Section>

        <Section id="accounts" title="4. User Accounts and Registration">
          <p>
            To access the Service you must create an account by providing accurate, current, and
            complete information. You agree to:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Provide truthful registration information and keep it up to date</li>
            <li>
              Maintain the confidentiality of your password and not share your credentials with any
              third party
            </li>
            <li>Notify us immediately of any unauthorised access to your account</li>
            <li>Accept responsibility for all activities that occur under your account</li>
          </ul>
          <p>
            We reserve the right to suspend or terminate accounts that provide false information,
            violate these Terms, or are inactive for extended periods.
          </p>
          <p>
            Tenant accounts may be created by invitation from a registered landlord. By accepting an
            invitation you confirm that you are the named tenant at the property indicated.
          </p>
        </Section>

        <Section id="acceptable-use" title="5. Acceptable Use">
          <p>You agree not to use the Service to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>
              Violate any applicable local, state, national, or international law or regulation
            </li>
            <li>Harass, abuse, threaten, or harm any person</li>
            <li>
              Upload or transmit viruses, malware, or other malicious code
            </li>
            <li>
              Attempt to gain unauthorised access to any part of the Service or its infrastructure
            </li>
            <li>
              Reverse-engineer, decompile, or disassemble any part of the Service
            </li>
            <li>
              Scrape, crawl, or systematically extract data from the Service without written
              permission
            </li>
            <li>
              Impersonate any person or entity, or falsely represent your affiliation with any
              person or entity
            </li>
            <li>
              Use the AI features to generate spam, misinformation, discriminatory content, or
              content that violates fair housing laws
            </li>
            <li>
              Resell, sublicense, or provide the Service to third parties without our written
              consent
            </li>
          </ul>
          <p>
            We reserve the right to investigate and take appropriate action, including suspension or
            termination of your account and reporting to law enforcement, for any violation of these
            provisions.
          </p>
        </Section>

        <Section id="data-privacy" title="6. Data, Privacy and Security">
          <p className="font-medium text-gray-800">6.1 Your Data</p>
          <p>
            You retain ownership of all data, content, and information you upload or transmit
            through the Service ("User Data"). By using the Service you grant NivasaPro a limited,
            non-exclusive, worldwide licence to store, process, and transmit your User Data solely
            as necessary to provide and improve the Service.
          </p>
          <p className="font-medium text-gray-800">6.2 AI Training</p>
          <p>
            We do not use your User Data to train AI models without your explicit opt-in consent.
            Aggregated, anonymised, and de-identified data may be used to improve the Service.
          </p>
          <p className="font-medium text-gray-800">6.3 Communications Data</p>
          <p>
            SMS and messaging content processed through the Service (including conversations with
            Niva) is stored to maintain conversation history and generate case records. Tenant phone
            numbers are used solely for service-related communications and are not shared with
            third-party marketers.
          </p>
          <p className="font-medium text-gray-800">6.4 Security</p>
          <p>
            We implement industry-standard technical and organisational measures to protect your
            data. However, no method of transmission over the internet is 100% secure. You are
            responsible for maintaining the security of your account credentials.
          </p>
          <p>
            Our full Privacy Policy, which is incorporated into these Terms by reference, is
            available at {SITE_URL}/privacy.
          </p>
        </Section>

        <Section id="ip" title="7. Intellectual Property">
          <p>
            The Service, including all software, designs, text, graphics, AI models, and other
            content created by NivasaPro, is owned by or licensed to {COMPANY_NAME} and is
            protected by copyright, trademark, and other intellectual property laws.
          </p>
          <p>
            Subject to these Terms, we grant you a limited, non-exclusive, non-transferable,
            revocable licence to access and use the Service for your internal business or personal
            property management purposes.
          </p>
          <p>
            You may not copy, modify, distribute, sell, or lease any part of the Service, nor may
            you reverse-engineer or attempt to extract the source code of the Service.
          </p>
        </Section>

        <Section id="payment" title="8. Subscription and Payment">
          <p>
            Certain features of the Service require a paid subscription. By subscribing you agree
            to pay all applicable fees as described on our pricing page. All fees are:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Quoted and charged in United States Dollars (USD) unless stated otherwise</li>
            <li>Billed in advance on the applicable billing cycle (monthly or annual)</li>
            <li>Non-refundable except as required by applicable law or as stated in our Refund Policy</li>
          </ul>
          <p>
            We may change subscription fees with at least 30 days' notice. Continued use of the
            Service after a fee change constitutes your acceptance of the new fees. You may cancel
            your subscription at any time; cancellation takes effect at the end of the current
            billing period.
          </p>
          <p>
            During the current pilot / proof-of-concept phase the Service may be provided free of
            charge. We will notify pilot users before any transition to a paid tier.
          </p>
        </Section>

        <Section id="disclaimers" title="9. Disclaimers and Warranties">
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
            EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY,
            FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR THAT THE SERVICE WILL BE
            UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS.
          </p>
          <p>
            We do not warrant that: (a) the Service will meet your specific requirements; (b) any
            errors or defects in the Service will be corrected; (c) AI-generated outputs will be
            accurate or complete; or (d) information transmitted through the Service will be secure.
          </p>
          <p>
            Some jurisdictions do not allow the exclusion of certain warranties. In such
            jurisdictions, the above exclusions apply to the fullest extent permitted by law.
          </p>
        </Section>

        <Section id="liability" title="10. Limitation of Liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL {COMPANY_NAME.toUpperCase()},
            ITS DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, PARTNERS, OR LICENSORS BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES,
            INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR OTHER
            INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION WITH:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Your use of or inability to use the Service</li>
            <li>Any AI-generated decision, recommendation, or action taken by the agentic AI</li>
            <li>Unauthorised access to or alteration of your data</li>
            <li>Any third-party conduct or content on the Service</li>
            <li>Any bugs, viruses, or other harmful code transmitted through the Service</li>
          </ul>
          <p>
            OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATING TO THESE
            TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO US IN
            THE TWELVE MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED US DOLLARS ($100).
          </p>
        </Section>

        <Section id="indemnification" title="11. Indemnification">
          <p>
            You agree to indemnify, defend, and hold harmless {COMPANY_NAME}, its affiliates,
            officers, directors, employees, and agents from and against any claims, liabilities,
            damages, judgments, awards, losses, costs, expenses, or fees (including reasonable
            attorneys' fees) arising out of or relating to: (a) your violation of these Terms;
            (b) your User Data; (c) your use of the Service in a manner not authorised by these
            Terms; or (d) your violation of any third-party rights.
          </p>
        </Section>

        <Section id="termination" title="12. Termination">
          <p>
            Either party may terminate this agreement at any time. You may close your account by
            contacting us at {CONTACT_EMAIL}. We may suspend or terminate your access to the
            Service immediately, without prior notice or liability, for any reason, including if
            you breach these Terms.
          </p>
          <p>
            Upon termination: (a) your licence to use the Service immediately ceases; (b) you must
            cease all use of the Service; and (c) we may delete your User Data after a reasonable
            retention period, subject to our legal obligations. Sections relating to disclaimers,
            limitation of liability, indemnification, and governing law survive termination.
          </p>
        </Section>

        <Section id="governing-law" title="13. Governing Law and Dispute Resolution">
          <p>
            These Terms are governed by and construed in accordance with the laws of the State of
            Ohio, United States, without regard to its conflict of law provisions. You agree to
            submit to the personal and exclusive jurisdiction of the courts located in Franklin
            County, Ohio for resolution of any disputes.
          </p>
          <p>
            Before filing any legal action, you agree to attempt to resolve any dispute informally
            by contacting us at {CONTACT_EMAIL}. We will attempt in good faith to resolve the
            dispute within 30 days of receiving notice.
          </p>
        </Section>

        <Section id="changes" title="14. Changes to These Terms">
          <p>
            We reserve the right to modify these Terms at any time. If we make material changes we
            will notify you by email and/or by posting a prominent notice on the Service at least
            14 days before the changes take effect. Your continued use of the Service after the
            effective date of revised Terms constitutes your acceptance of the changes.
          </p>
          <p>
            We encourage you to review these Terms periodically. The "Last updated" date at the top
            of this page indicates when the Terms were most recently revised.
          </p>
        </Section>

        <Section id="contact" title="15. Contact Information">
          <p>
            If you have any questions about these Terms, please contact us:
          </p>
          <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm">
            <p className="font-semibold text-gray-800">{COMPANY_NAME}</p>
            <p>Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-600 hover:underline">{CONTACT_EMAIL}</a></p>
            <p>Website: <a href={SITE_URL} className="text-cyan-600 hover:underline">{SITE_URL}</a></p>
          </div>
        </Section>

        <div className="mt-10 pt-6 border-t border-gray-200 text-center">
          <RouterLink
            to="/register"
            className="inline-block px-6 py-2.5 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700 transition-colors"
          >
            Back to Registration
          </RouterLink>
          <p className="mt-3 text-xs text-gray-400">
            © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
